import React, { useState, useEffect, useCallback } from "react";
import { FaPlay, FaWrench, FaTint, FaLightbulb, FaHammer, FaExternalLinkAlt, FaEye, FaThumbsUp, FaYoutube } from "react-icons/fa";
import { YOUTUBE_API_KEY as YT_API_KEY } from '../config'

const SEARCH_QUERIES = {
  US: {
    plumbing: "apartment plumbing DIY fix tutorial",
    electrical: "apartment electrical DIY fix tutorial safe",
    general: "apartment home repair DIY tutorial easy",
  },
  IN: {
    plumbing: "apartment plumbing fix DIY Hindi tutorial",
    electrical: "apartment electrical repair DIY Hindi safe",
    general: "apartment home repair DIY Hindi tutorial",
  },
};

function detectRegion() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.startsWith("Asia/Kolkata") || tz.startsWith("Asia/Calcutta") || tz.startsWith("Asia/Chennai")) return "IN";
    const lang = navigator.language || "";
    if (lang.startsWith("hi") || lang.startsWith("ta") || lang.startsWith("te") || lang.startsWith("kn") || lang.startsWith("ml")) return "IN";
  } catch {}
  return "US";
}

function formatCount(n) {
  if (!n) return "";
  const num = parseInt(n, 10);
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  return num.toString();
}

const categories = [
  { id: "all", label: "All", icon: FaHammer },
  { id: "plumbing", label: "Plumbing", icon: FaTint },
  { id: "electrical", label: "Electrical", icon: FaLightbulb },
  { id: "general", label: "General", icon: FaWrench },
];

const categoryColor = {
  plumbing: "bg-blue-100 text-blue-800",
  electrical: "bg-yellow-100 text-yellow-800",
  general: "bg-gray-100 text-gray-800",
};

const categoryIcon = {
  plumbing: <FaTint size={12} />,
  electrical: <FaLightbulb size={12} />,
  general: <FaWrench size={12} />,
};

async function fetchYouTubeVideos(query, regionCode, maxResults = 3) {
  // Step 1: search for videos sorted by relevance (YouTube's default ranking considers views, likes, etc.)
  const searchParams = new URLSearchParams({
    part: "snippet",
    q: query,
    type: "video",
    maxResults: String(maxResults),
    order: "relevance",
    regionCode,
    safeSearch: "strict",
    videoEmbeddable: "true",
    key: YT_API_KEY,
  });

  const searchRes = await fetch(`https://www.googleapis.com/youtube/v3/search?${searchParams}`);
  if (!searchRes.ok) throw new Error(`YouTube search failed: ${searchRes.status}`);
  const searchData = await searchRes.json();

  const ids = (searchData.items || []).map((it) => it.id.videoId).filter(Boolean);
  if (ids.length === 0) return [];

  // Step 2: get full statistics (viewCount, likeCount) for accurate ranking
  const statsParams = new URLSearchParams({
    part: "snippet,statistics",
    id: ids.join(","),
    key: YT_API_KEY,
  });

  const statsRes = await fetch(`https://www.googleapis.com/youtube/v3/videos?${statsParams}`);
  if (!statsRes.ok) throw new Error(`YouTube stats failed: ${statsRes.status}`);
  const statsData = await statsRes.json();

  return (statsData.items || [])
    .map((v) => ({
      id: v.id,
      title: v.snippet.title,
      description: v.snippet.description?.slice(0, 120) + (v.snippet.description?.length > 120 ? "…" : ""),
      channelTitle: v.snippet.channelTitle,
      thumbnail: v.snippet.thumbnails?.medium?.url || v.snippet.thumbnails?.default?.url || "",
      viewCount: v.statistics?.viewCount || "0",
      likeCount: v.statistics?.likeCount || "0",
      videoUrl: `https://www.youtube.com/watch?v=${v.id}`,
    }))
    .sort((a, b) => parseInt(b.viewCount, 10) - parseInt(a.viewCount, 10));
}

export default function DIYTutorials() {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [region, setRegion] = useState("US");
  const [videos, setVideos] = useState({ plumbing: [], electrical: [], general: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadVideos = useCallback(async (reg) => {
    if (!YT_API_KEY) {
      setError("YouTube API key not configured");
      return;
    }
    setLoading(true);
    setError("");

    const cacheKey = `diy_videos_${reg}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        setVideos(JSON.parse(cached));
        setLoading(false);
        return;
      } catch {}
    }

    try {
      const queries = SEARCH_QUERIES[reg] || SEARCH_QUERIES.US;
      const [plumbing, electrical, general] = await Promise.all([
        fetchYouTubeVideos(queries.plumbing, reg, 3),
        fetchYouTubeVideos(queries.electrical, reg, 3),
        fetchYouTubeVideos(queries.general, reg, 3),
      ]);
      const result = { plumbing, electrical, general };
      setVideos(result);
      sessionStorage.setItem(cacheKey, JSON.stringify(result));
    } catch (e) {
      console.error("DIY fetch error:", e);
      setError("Failed to load videos. Please try again.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const r = detectRegion();
    setRegion(r);
    loadVideos(r);
  }, [loadVideos]);

  const allVideos = [
    ...videos.plumbing.map((v) => ({ ...v, category: "plumbing" })),
    ...videos.electrical.map((v) => ({ ...v, category: "electrical" })),
    ...videos.general.map((v) => ({ ...v, category: "general" })),
  ];

  const filteredVideos =
    selectedCategory === "all"
      ? allVideos
      : allVideos.filter((v) => v.category === selectedCategory);

  return (
    <div className="p-4">
      <div className="mb-4">
        <h2 className="text-lg font-bold mb-1">DIY Tutorials</h2>
        <p className="text-gray text-sm font-bold">
          Top-rated videos for common apartment fixes
          <span className="ml-2 text-xs text-gray-400 font-bold">
            ({region === "IN" ? "🇮🇳 India" : "🇺🇸 US"} region)
          </span>
        </p>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
        {categories.map((cat) => {
          const Icon = cat.icon;
          const isActive = selectedCategory === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm whitespace-nowrap ${
                isActive
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              <Icon size={14} />
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-12 text-gray-500">Loading top-rated videos…</div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* No API key message */}
      {!YT_API_KEY && !loading && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          <p className="font-semibold mb-1">YouTube API Key Required</p>
          <p>Add <code className="bg-blue-100 px-1 rounded">VITE_YOUTUBE_API_KEY=your_key</code> to your <code className="bg-blue-100 px-1 rounded">.env</code> file.</p>
          <p className="mt-1 text-xs text-blue-600">Get a free key from the Google Cloud Console → APIs → YouTube Data API v3.</p>
        </div>
      )}

      {/* Video Cards */}
      {!loading && filteredVideos.length > 0 && (
        <div className="space-y-3">
          {filteredVideos.map((video) => (
            <a
              key={video.id}
              href={video.videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-blue-300 transition-colors"
            >
              {/* Real YouTube Thumbnail */}
              <div className="relative">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-44 object-cover bg-gray-100"
                  loading="lazy"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-black/60 p-3 rounded-full">
                    <FaPlay size={20} className="text-white ml-0.5" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="font-semibold text-sm mb-1 line-clamp-2 flex items-start gap-2">
                  <span className="flex-1">{video.title}</span>
                  <FaExternalLinkAlt size={10} className="text-gray-400 flex-shrink-0 mt-1" />
                </h3>

                <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                  <FaYoutube size={12} className="text-red-500" />
                  {video.channelTitle}
                </p>

                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${categoryColor[video.category]}`}>
                    {categoryIcon[video.category]}
                    {video.category.charAt(0).toUpperCase() + video.category.slice(1)}
                  </span>
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <FaEye size={11} /> {formatCount(video.viewCount)}
                  </span>
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <FaThumbsUp size={11} /> {formatCount(video.likeCount)}
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && YT_API_KEY && filteredVideos.length === 0 && (
        <div className="text-center py-12 text-gray-400">No videos found for this category.</div>
      )}

      {/* Safety Disclaimer */}
      <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-900">
          ⚠️ <strong>Safety First:</strong> If you're unsure about any repair,
          please contact an approved service professional from the Contacts
          section. Always turn off electricity before electrical work.
        </p>
      </div>
    </div>
  );
}
